import React, { useContext, useMemo } from 'react';
import { Button, Typography } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import DeviceEndOfSupportContext from '@totem/components/deviceEndOfSupport/deviceEndOfSupportContext';
import { NOT_RECORDED } from '@totem/components/deviceEndOfSupport/utilities';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';

type BuildingRow = {
  name: string;
  region: string;
  past: number;
  soon: number;
  ok: number;
  none: number;
  total: number;
};

const countCell = (value: number, type?: 'danger' | 'warning') =>
  value ? (
    <Typography.Text type={type} strong={Boolean(type)}>
      {value}
    </Typography.Text>
  ) : (
    <Typography.Text type="secondary">0</Typography.Text>
  );

const byCount =
  (key: keyof BuildingRow) => (left: BuildingRow, right: BuildingRow) =>
    (left[key] as number) - (right[key] as number);

export const useBuildingRows = () => {
  const { rows } = useContext(DeviceEndOfSupportContext);

  return useMemo(() => {
    const buildings = new Map<string, BuildingRow>();
    rows.forEach((row) => {
      const name = row.building || NOT_RECORDED;
      const building = buildings.get(name) ?? {
        name,
        region: row.region,
        past: 0,
        soon: 0,
        ok: 0,
        none: 0,
        total: 0,
      };
      building[row.status === 'check' ? 'none' : row.status] += 1;

      building.total += 1;

      buildings.set(name, building);
    });
    // Most urgent first: past end of service, then ending soon.
    return [...buildings.values()].sort(
      (left, right) =>
        right.past - left.past ||
        right.soon - left.soon ||
        left.name.localeCompare(right.name),
    );
  }, [rows]);
};

const BuildingTable = () => {
  const { loading, resetAll, setFilter, setActiveTab } = useContext(
    DeviceEndOfSupportContext,
  );
  const buildings = useBuildingRows();

  const showBuilding = (name: string) => {
    resetAll();

    setFilter('building', [name]);

    setActiveTab('devices');
  };

  const columns: ColumnProps<BuildingRow>[] = [
    {
      title: 'Building',
      key: 'name',
      sorter: (left, right) => left.name.localeCompare(right.name),
      render: (_, building) => (
        <>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', fontWeight: 600 }}
            onClick={() => showBuilding(building.name)}
          >
            {building.name}
          </Button>
          {building.region && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {building.region}
              </Typography.Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: 'Past end of service',
      key: 'past',
      align: 'right',
      sorter: byCount('past'),
      render: (_, building) => countCell(building.past, 'danger'),
    },
    {
      title: 'Ending within 12 months',
      key: 'soon',
      align: 'right',
      sorter: byCount('soon'),
      render: (_, building) => countCell(building.soon, 'warning'),
    },
    {
      title: 'In service',
      key: 'ok',
      align: 'right',
      sorter: byCount('ok'),
      render: (_, building) => countCell(building.ok),
    },
    {
      title: 'No date on file',
      key: 'none',
      align: 'right',
      sorter: byCount('none'),
      render: (_, building) => countCell(building.none),
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      sorter: byCount('total'),
      render: (_, building) => <strong>{building.total}</strong>,
    },
  ];

  return (
    <>
      <Typography.Paragraph type="secondary">
        Where the equipment that needs attention is. Click a building to see its
        devices.
      </Typography.Paragraph>
      <IBTable
        columns={columns}
        dataSource={buildings}
        loading={loading}
        rowKey="name"
        pagination={{ defaultPageSize: 25, showSizeChanger: true }}
      />
    </>
  );
};

export default BuildingTable;
